import { ChangeReferenceDto } from 'src/app/data-transfer/entities/change-reference-dto';
import { ImportTypeSetting, PackagingImportHelper } from './packaging-import-helper';
import { Observable } from 'rxjs';
import { ImportCandidateDto } from 'src/app/data-transfer/entities/import-candidate-dto';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ComponentNavigationService } from 'src/app/navigation/services/navigation-services/component-navigation-service';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { ImportComponentCandidateDto } from 'src/app/data-transfer/entities/component-entities/import-component-candidate-dto';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';

export class PackagingComponentImportHelper implements PackagingImportHelper {
  private readonly importCandidatesDataSource = new PaginationDataSource<ImportCandidateDto>(query =>
    this.importExportApiService.getImportCandidatesByPage(PackagingPart.Component, query)
  );

  private readonly changeCandidatesDataSource = new PaginationDataSource<ChangeReferenceDto>(query =>
    this.importExportApiService.getChangesByPage(PackagingPart.Component, query)
  );

  constructor(
    private importExportApiService: ImportExportApiService,
    private componentNavigationService: ComponentNavigationService,
    private componentTypeService: ComponentTypeService
  ) {}

  getImportCandidatesDataSource(): PaginationDataSource<ImportCandidateDto> {
    return this.importCandidatesDataSource;
  }

  getChangeCandidatesDataSource(): PaginationDataSource<ChangeReferenceDto> {
    return this.changeCandidatesDataSource;
  }

  getTableTitle(importType: ImportTypeSetting, archiveActive: boolean): string {
    if (importType === ImportTypeSetting.Import) {
      if(archiveActive) {
        return 'dataManagement.import.import.tableTitleComponentArchive';
      }
      return 'dataManagement.import.import.tableTitleComponent';
    }
    return 'dataManagement.import.changes.tableTitleComponent';
  }

  getDisplayedColumns(): string[] {
    return [
      'itemId',
      'version',
      'exportingOrganization',
      'packagingComponentCategoryName',
      'packagingComponentSubtypeName',
      'articleName',
      'articleNumber',
      'timestamp',
      'action',
      'select',
    ];
  }

  packagingImportCandidatesSeen(): Promise<any> {
    return this.importExportApiService.putComponentImportNotificationsSeen();
  }

  packagingChangeCandidatesSeen(): Promise<any> {
    return this.importExportApiService.putComponentTrackingtNotificationsSeen();
  }

  packagingImportCandidatesCountUpdate(): Promise<any> {
    return Promise.all([this.importExportApiService.updateComponentImportCount(),this.importExportApiService.updateMaterialImportCount()]);
  }

  packagingChangeCandidatesCountUpdate(): Promise<any> {
    return Promise.all([this.importExportApiService.updateComponentTrackingCount(),this.importExportApiService.updateMaterialTrackingCount()]);
  }

  getDeleteFunction(importType: ImportTypeSetting): (candidate: number) => Observable<any> {
    if (importType === ImportTypeSetting.Import) {
      return this.getDeleteFunctionImportCandidate();
    }
    return this.getDeleteFunctionChangeCandidate();
  }

  getImportFunctionImportCandidate(): (candidateId: number, targetDirectoryId: number, isWithTracking: boolean) => Observable<any> {
    return (candidateId: number, targetDirectoryId: number, isWithTracking: boolean) =>
      this.importExportApiService.importComponents(candidateId, targetDirectoryId, isWithTracking);
  }

  getChangeFunctionChangeCandidate(): (changeId: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.applyComponentChanges(changeId);
  }

  navigateToCandidate(candidate: ImportCandidateDto): void {
    const componentCandidate = candidate as ImportComponentCandidateDto;
    const componentTypeName = this.componentTypeService.getComponentTypeNameById(componentCandidate.packagingComponent.packagingComponentTypeId);
    if (componentCandidate.packagingComponent.packagingComponentCategoryId === PackagingComponentTypesEnum.Decoration) {
      this.componentNavigationService.navigateToDecorationPreview(componentTypeName, candidate.id);
    } else {
      this.componentNavigationService.navigateToComponentPreview(componentTypeName, candidate.id);
    }
  }

  navigateToChange(candidate: ChangeReferenceDto, seePackagingBeforeChange: boolean): void {
    const id = seePackagingBeforeChange ? candidate.id : candidate.importCandidateId;
    const component = seePackagingBeforeChange ? candidate.existing : candidate.updated;
    const componentTypeId = component.packagingComponentTypeId;
    if (componentTypeId == null) {
      return;
    }

    const componentTypeName = this.componentTypeService.getComponentTypeNameById(componentTypeId);
    if (component.packagingComponentCategoryId === PackagingComponentTypesEnum.Decoration) {
      this.componentNavigationService.navigateToDecorationPreview(componentTypeName, id, true, seePackagingBeforeChange);
    } else {
      this.componentNavigationService.navigateToComponentPreview(componentTypeName, id, true, seePackagingBeforeChange);
    }
  }

  private getDeleteFunctionImportCandidate(): (candidate: number) => Observable<any> {
    return (candidateId: number) => this.importExportApiService.deleteComponentImportCandidate(candidateId);
  }

  private getDeleteFunctionChangeCandidate(): (candidate: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.ignoreComponentChanges(changeId);
  }
}
